import { ElementType, memo, useEffect, useRef, useState } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AbsenceResponse,
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';

import { PRIMARY, RED } from 'constants/colors';
import { brightness } from 'utils/misc';
import AbsenceTooltipBody from '../components/AbsenceTooltipBody';

interface AbsencePragmaticProps {
  id: string;
  absence: AbsenceResponse;
  component?: ElementType<any>;
  showTime?: boolean;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  scheduleStart: string;
}

const AbsencePragmatic = ({
  id,
  absence,
  component,
  showTime,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  scheduleStart
}: AbsencePragmaticProps) => {
  const { t } = useTranslation('', { keyPrefix: 'activityPlanning' });
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !showTime) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        id,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier,
        scheduleStart,
        isDroppableCell: true
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });

    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    id,
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    scheduleStart,
    showTime
  ]);

  return (
    <Tooltip
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      open={hovered}
      title={<AbsenceTooltipBody type={absence?.type} />}
    >
      <Box
        ref={ref}
        component={component || 'div'}
        display="flex"
        alignItems="center"
        sx={{
          boxSizing: 'border-box',
          color: '#023850',
          backgroundColor: brightness(RED, 90),
          border: `2px solid transparent`,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          borderRadius: '6px',
          width: '100%',
          height: '40px',
          padding: '0 10px',
          '+ *': {
            marginTop: '8px'
          },
          '&[data-drag-over]': {
            color: PRIMARY,
            backgroundColor: brightness(PRIMARY, 90),
            borderColor: PRIMARY
          }
        }}
      >
        {!showTime
          ? t('fullDayAbsent')
          : t('absentFromTil', {
              from: dayjs(absence.start).format('HH:mm'),
              to: dayjs(absence.end).format('HH:mm')
            })}
      </Box>
    </Tooltip>
  );
};

export default memo(AbsencePragmatic);
